import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    // Wait for images so widths are valid on mobile before initializing
    const images = Array.from(this.querySelectorAll('img'));
    const pending = images.filter((img) => !img.complete || img.naturalWidth === 0);

    if (pending.length > 0) {
      let remaining = pending.length;
      const done = () => {
        remaining -= 1;
        if (remaining === 0) {
          requestAnimationFrame(() => this.#initializeWithRetry());
        }
      };
      for (const img of pending) {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    } else {
      // Layout-ready path
      requestAnimationFrame(() => this.#initializeWithRetry());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * Initialize the marquee, retrying if dimensions aren't ready
   */
  #initializeWithRetry(attempt = 0) {
    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 0;

    // If dimensions aren't ready and we haven't exceeded max attempts, retry
    if ((marqueeWidth === 0 || marqueeRepeatedItemWidth === 0) && attempt < 5) {
      requestAnimationFrame(() => {
        this.#initializeWithRetry(attempt + 1);
      });
      return;
    }

    // Proceed with initialization
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  #setSpeed(value = this.#calculateSpeed()) {
    // Ensure speed is never 0 or invalid
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const validSpeed = value > 0 ? value : speedFactor;
    this.style.setProperty('--marquee-speed', `${validSpeed}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;

    // If dimensions are invalid, return a safe default speed
    if (marqueeWidth === 0 || marqueeRepeatedItemWidth === 0) {
      return speedFactor;
    }

    const count = Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
    const speed = Math.sqrt(count) * speedFactor;
    
    // Ensure speed is never 0
    return speed > 0 ? speed : speedFactor;
  }

  #handleResize = debounce(() => {
    this.#handleResizeWithRetry(0);
  }, 250);

  #handleResizeWithRetry(attempt = 0) {
    const { marqueeItems } = this.refs;
    
    // Safety check: ensure we have items
    if (marqueeItems.length === 0) {
      // Try to reinitialize if we lost our items
      this.#initializeWithRetry();
      return;
    }
    
    // Ensure dimensions are valid before proceeding
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 0;
    
    // Maximum retry limit to prevent infinite loops
    const MAX_RETRIES = 10;
    if ((marqueeWidth === 0 || marqueeRepeatedItemWidth === 0) && attempt < MAX_RETRIES) {
      // If dimensions aren't ready, try again on next frame
      requestAnimationFrame(() => {
        if (this.isConnected) {
          this.#handleResizeWithRetry(attempt + 1);
        }
      });
      return;
    }

    // If we've exceeded max retries, use safe defaults
    if (attempt >= MAX_RETRIES) {
      console.warn('Marquee: Resize handler exceeded max retries, using safe defaults');
      return;
    }

    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = marqueeItems.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      // Ensure we never remove all items - always keep at least the original items
      const itemsToRemove = Math.min(
        currentNumberOfCopies - newNumberOfCopies,
        currentNumberOfCopies - 1 // Always keep at least 1 item
      );
      if (itemsToRemove > 0) {
        this.#removeRepeatedItems(itemsToRemove);
      }
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;

    // Safety check: ensure we have items before adding
    if (!marqueeItems[0]) {
      console.warn('Marquee: Cannot add items, no original items available');
      return;
    }

    // Ensure we're adding at least 1 copy (numberOfCopies should be >= 1)
    const copiesToAdd = Math.max(1, numberOfCopies) - 1;

    for (let i = 0; i < copiesToAdd; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;

    // Safety check: ensure we have items before removing
    if (marqueeItems.length === 0) {
      console.warn('Marquee: Cannot remove items, no items available');
      return;
    }

    // Find the original marquee__repeated-items container to protect it
    // marqueeItems[0] is the original container div with class "marquee__repeated-items"
    const originalContainer = marqueeItems[0];
    if (!originalContainer) {
      console.warn('Marquee: Cannot find original container');
      return;
    }

    // Only remove cloned items, never the original container
    // Cloned items are siblings of the original container within content
    // We need to ensure we always keep at least the original container
    let removed = 0;
    const maxRemovals = Math.min(numberOfCopies, content.children.length - 1); // Always keep at least 1 child (the original)
    
    while (removed < maxRemovals && content.children.length > 1) {
      const lastChild = content.lastElementChild;
      
      // Never remove the original container - it must always remain
      if (lastChild === originalContainer) {
        // If the last child is the original, we can't remove any more
        break;
      }
      
      // Only remove cloned items (siblings of the original container)
      if (lastChild && lastChild !== originalContainer) {
        lastChild.remove();
        removed++;
      } else {
        break;
      }
    }
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    
    // Safety check: ensure we have items
    if (marqueeItems.length === 0) {
      return 1; // Return minimum of 1 to ensure we always have items
    }
    
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;

    // Ensure we always return at least 1 copy
    if (marqueeRepeatedItemWidth === 0 || marqueeWidth === 0) {
      return 1;
    }
    
    const calculated = Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
    // Ensure minimum of 1 to always have at least the original items
    return Math.max(1, calculated);
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
